import {FC} from 'react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {Text} from '@/components/ui/text/Text'
import {DayOfWeek, TimeSlot, UserAvailability, dayOfWeekTranslationKeys} from '@/features/users-availability/types'
import {useTranslation} from '@/translations/i18n'
import {Divider} from '@/components/ui/divider/Divider'
import {StyledTimeSlotRowContainer} from './style'

export const TimeSlotDisplay: FC<{slot: TimeSlot}> = ({slot}) => (
    <Grid columns={5} gap={2} fullWidth>
        <GridItem spanColumn={2}>
            <Text size="textMd">{slot.start}</Text>
        </GridItem>
        <GridItem spanColumn={1}>
            <Text size="textMd" palette="neutral" shade="500">
                {' '}
                →{' '}
            </Text>
        </GridItem>
        <GridItem spanColumn={2}>
            <Text size="textMd">{slot.end}</Text>
        </GridItem>
    </Grid>
)
TimeSlotDisplay.displayName = 'TimeSlotDisplay'

export const DayLabel: FC<{day: DayOfWeek}> = ({day}) => {
    const {t} = useTranslation()
    return (
        <Text size="textMd" weight="500">
            {t(dayOfWeekTranslationKeys[day])}
        </Text>
    )
}
DayLabel.displayName = 'DayLabel'

// Component compositions
export const NoAvailabilityContent: FC = () => {
    const {t} = useTranslation()
    return (
        <Text palette="neutral" shade="400" size="textMd">
            {t('commons:no_availability')}
        </Text>
    )
}
NoAvailabilityContent.displayName = 'NoAvailabilityContent'

export const AllDayAvailabilityContent: FC = () => {
    const {t} = useTranslation()
    return (
        <Text palette="neutral" shade="600" size="textMd">
            {t('commons:all_day_availability')}
        </Text>
    )
}
AllDayAvailabilityContent.displayName = 'AllDayAvailabilityContent'

export const TimeSlotsContent: FC<{timeSlots: TimeSlot[]}> = ({timeSlots}) => (
    <Flexbox direction="column" align="center" gap={2} fullWidth>
        {timeSlots.map((slot, index) => (
            <TimeSlotDisplay key={index} slot={slot} />
        ))}
    </Flexbox>
)
TimeSlotsContent.displayName = 'TimeSlotsContent'

export const AvailabilityContent: FC<{
    enabled: boolean
    timeSlots: TimeSlot[]
}> = ({enabled, timeSlots}) => {
    if (!enabled) {
        return <NoAvailabilityContent />
    }

    if (timeSlots.length === 0) {
        return <AllDayAvailabilityContent />
    }

    return <TimeSlotsContent timeSlots={timeSlots} />
}
AvailabilityContent.displayName = 'AvailabilityContent'

// Default availability object defined outside component to avoid recreating it on each render
const defaultAvailability = {enabled: false, timeSlots: []}

export const DayRow: FC<{
    day: DayOfWeek
    dayAvailability: UserAvailability[DayOfWeek] | undefined
    isLast: boolean
}> = ({day, dayAvailability, isLast}) => {
    // Use the predefined default value
    const availability = dayAvailability || defaultAvailability

    return (
        <>
            <GridItem spanColumn={2} gap={4}>
                <StyledTimeSlotRowContainer columns={2} gap={4}>
                    <GridItem spanColumn={1} gap={4}>
                        <DayLabel day={day} />
                    </GridItem>
                    <GridItem spanColumn={1} gap={4}>
                        <AvailabilityContent enabled={availability.enabled} timeSlots={availability.timeSlots} />
                    </GridItem>
                </StyledTimeSlotRowContainer>
            </GridItem>
            {!isLast && (
                <GridItem spanColumn={2}>
                    <Divider direction="horizontal" />
                </GridItem>
            )}
        </>
    )
}
DayRow.displayName = 'DayRow'
